import {Component, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {DataService} from '../services/data.service';
import {RestService} from '../services/rest.service';
import {UIBlockerService} from '../services/uiblocker.service';
import {finalize} from 'rxjs/operators';
import {isNullOrUndefined} from '../shared/is-null-or-undefined';
import {getOrderSourceLabel} from '../services/order-source-labels';

interface PlatformChartBar {
    key: string;
    label: string;
    value: number;
    amount?: number;
    height: number;
    color: string;
}

interface PlatformChartDay {
    date: string;
    label: string;
    total: number;
    bars: PlatformChartBar[];
}

interface PlatformLegendItem {
    key: string;
    label: string;
    color: string;
}

interface TodayPlatformBar {
    key: string;
    label: string;
    value: number;
    amount?: number;
    width: number;
    color: string;
}

interface PaymentMethodChartDay {
    date: string;
    label: string;
    total: number;
    totalAmount: number;
    cash: number;
    cashAmount: number;
    card: number;
    cardAmount: number;
    voucher: number;
    voucherAmount: number;
    bars: PlatformChartBar[];
}

@Component({
    selector: 'app-overview',
    templateUrl: './overview.component.html',
    styleUrls: ['./overview.component.css']
})
export class OverviewComponent implements OnInit, OnDestroy {

    overviewData: any;
    onlineCars: any;
    totalOrderReports: PlatformChartDay[];
    platformLegend: PlatformLegendItem[] = [];
    todayPlatformBars: TodayPlatformBar[] = [];
    paymentMethodReports: PaymentMethodChartDay[];
    paymentMethodLegend: PlatformLegendItem[] = [];
    todayPaymentMethodBars: TodayPlatformBar[] = [];
    todayPaymentOrders = 0;
    todayPaymentAmount = 0;
    hasPlatformOrders = false;
    hasTodayOrders = false;
    hasPaymentMethodOrders = false;
    hasTodayPaymentMethodOrders = false;
    isLoadingOrderReports = false;
    isLoadingPaymentMethodReports = false;
    dataChangedOverviewSubscription: Subscription;
    dataChangedOnlineCarsSubscription: Subscription;
    dataChangedSessionSubscription: Subscription;

    constructor(
        private dataService: DataService,
        private restService: RestService,
        private uiBlockerService: UIBlockerService,
    ) { /* noop */
    }

    getTotalOrdersReport() {
        if (this.isLoadingOrderReports) {
            return;
        }

        this.isLoadingOrderReports = true;
        this.totalOrderReports = undefined;
        this.platformLegend = [];
        this.todayPlatformBars = [];
        this.hasPlatformOrders = false;
        this.hasTodayOrders = false;
        this.restService.getOverviewOrdersByPlatform()
            .pipe(
                finalize(() => {
                    this.isLoadingOrderReports = false;
                    this.uiBlockerService.unBlockUI();
                })
            )
            .subscribe((res: any) => {
                this.sortTotalReports(res ? res.Report : []);
            }, () => {
                this.totalOrderReports = [];
                this.platformLegend = [];
                this.todayPlatformBars = [];
                this.hasPlatformOrders = false;
                this.hasTodayOrders = false;
            });

    }

    getPaymentMethodOrdersReport() {
        if (this.isLoadingPaymentMethodReports) {
            return;
        }

        this.isLoadingPaymentMethodReports = true;
        this.paymentMethodReports = undefined;
        this.paymentMethodLegend = [];
        this.todayPaymentMethodBars = [];
        this.todayPaymentOrders = 0;
        this.todayPaymentAmount = 0;
        this.hasPaymentMethodOrders = false;
        this.hasTodayPaymentMethodOrders = false;
        this.restService.getOverviewOrdersByPaymentMethod()
            .pipe(
                finalize(() => {
                    this.isLoadingPaymentMethodReports = false;
                    this.uiBlockerService.unBlockUI();
                })
            )
            .subscribe((res: any) => {
                this.sortPaymentMethodReports(res ? res.Report : []);
            }, () => {
                this.paymentMethodReports = [];
                this.paymentMethodLegend = [];
                this.todayPaymentMethodBars = [];
                this.todayPaymentOrders = 0;
                this.todayPaymentAmount = 0;
                this.hasPaymentMethodOrders = false;
                this.hasTodayPaymentMethodOrders = false;
            });
    }

    sortTotalReports(report) {
        const reportsByDate: { [date: string]: { [platform: string]: number } } = {};
        const platformTotals: { [platform: string]: number } = {};

        if (!Array.isArray(report)) {
            this.totalOrderReports = [];
            this.platformLegend = [];
            this.todayPlatformBars = [];
            this.hasPlatformOrders = false;
            this.hasTodayOrders = false;
            return;
        }

        report.forEach((item) => {
            const date = String(item.Date || '');
            if (date === '') {
                return;
            }
            const values = reportsByDate[date] || {};
            const platformTotals = Array.isArray(item.PlatformTotals) ? item.PlatformTotals : [];
            platformTotals.forEach((platformTotal) => {
                const platform = this.normalizePlatform(platformTotal.Platform);
                const total = Number(platformTotal.Total || 0);
                values[platform] = (values[platform] || 0) + total;
            });
            reportsByDate[date] = values;
        });

        Object.keys(reportsByDate).forEach((date) => {
            Object.keys(reportsByDate[date]).forEach((platform) => {
                platformTotals[platform] = (platformTotals[platform] || 0) + reportsByDate[date][platform];
            });
        });

        const platforms = Object.keys(platformTotals)
            .filter((platform) => platformTotals[platform] > 0)
            .sort((left, right) => this.getPlatformOrder(left) - this.getPlatformOrder(right)
                || left.localeCompare(right));

        this.platformLegend = platforms.map((platform, index) => ({
            key: platform,
            label: this.getPlatformLabel(platform),
            color: this.getPlatformColor(platform, index)
        }));

        let maximum = 0;
        Object.keys(reportsByDate).forEach((date) => {
            Object.keys(reportsByDate[date]).forEach((platform) => {
                maximum = Math.max(maximum, reportsByDate[date][platform]);
            });
        });

        this.totalOrderReports = this.getLastSevenDays().map((day) => {
            const date = this.getLocalDateKey(day);
            const values = reportsByDate[date] || {};
            const bars = this.platformLegend.map((platform) => {
                const value = values[platform.key] || 0;
                return {
                    key: platform.key,
                    label: platform.label,
                    value,
                    height: value === 0 || maximum === 0 ? 0 : Math.max(3, (value / maximum) * 100),
                    color: platform.color
                };
            });

            return {
                date,
                label: day.toLocaleDateString(undefined, {
                    weekday: 'short',
                    day: '2-digit',
                    month: 'short'
                }),
                total: bars.reduce((sum, bar) => sum + bar.value, 0),
                bars
            };
        });
        this.hasPlatformOrders = maximum > 0;

        const today = this.totalOrderReports[this.totalOrderReports.length - 1];
        const todayMaximum = today
            ? today.bars.reduce((maximumValue, bar) => Math.max(maximumValue, bar.value), 0)
            : 0;
        this.todayPlatformBars = today
            ? today.bars
                .filter((bar) => bar.value > 0)
                .map((bar) => ({
                    key: bar.key,
                    label: bar.label,
                    value: bar.value,
                    width: todayMaximum === 0 ? 0 : (bar.value / todayMaximum) * 100,
                    color: bar.color
                }))
            : [];
        this.hasTodayOrders = todayMaximum > 0;
    }

    sortPaymentMethodReports(report: any) {
        const methods = [
            {key: 'cash', label: 'Cash', color: '#4ade80'},
            {key: 'card', label: 'Card', color: '#60a5fa'},
            {key: 'voucher', label: 'Voucher', color: '#facc15'}
        ];
        const reportsByDate: {
            [date: string]: {
                totalOrders: number;
                totalAmount: number;
                methods: { [paymentMethod: string]: { orders: number; amount: number } };
            }
        } = {};

        if (!Array.isArray(report)) {
            report = [];
        }

        report.forEach((item) => {
            const date = String(item.Date || '');
            if (date === '') {
                return;
            }
            const values = {
                totalOrders: Number(item.TotalOrders || 0),
                totalAmount: Number(item.TotalAmount || 0),
                methods: {}
            };
            const methodTotals = Array.isArray(item.PaymentMethodTotals) ? item.PaymentMethodTotals : [];
            methodTotals.forEach((methodTotal) => {
                const method = String(methodTotal.PaymentMethod || '').trim().toLowerCase();
                if (method === '') {
                    return;
                }
                values.methods[method] = {
                    orders: Number(methodTotal.TotalOrders || 0),
                    amount: Number(methodTotal.TotalAmount || 0)
                };
            });
            reportsByDate[date] = values;
        });

        this.paymentMethodLegend = methods;
        let maximum = 0;
        Object.keys(reportsByDate).forEach((date) => {
            methods.forEach((method) => {
                const value = reportsByDate[date].methods[method.key];
                maximum = Math.max(maximum, value ? value.orders : 0);
            });
        });

        this.paymentMethodReports = this.getLastSevenDays().map((day) => {
            const date = this.getLocalDateKey(day);
            const values = reportsByDate[date] || {totalOrders: 0, totalAmount: 0, methods: {}};
            const bars = methods.map((method) => {
                const methodValue = values.methods[method.key];
                const value = methodValue ? methodValue.orders : 0;
                return {
                    key: method.key,
                    label: method.label,
                    value,
                    amount: methodValue ? methodValue.amount : 0,
                    height: value === 0 || maximum === 0 ? 0 : Math.max(3, (value / maximum) * 100),
                    color: method.color
                };
            });

            return {
                date,
                label: day.toLocaleDateString(undefined, {
                    weekday: 'short',
                    day: '2-digit',
                    month: 'short'
                }),
                total: values.totalOrders,
                totalAmount: values.totalAmount,
                cash: bars.find((bar) => bar.key === 'cash').value,
                cashAmount: bars.find((bar) => bar.key === 'cash').amount,
                card: bars.find((bar) => bar.key === 'card').value,
                cardAmount: bars.find((bar) => bar.key === 'card').amount,
                voucher: bars.find((bar) => bar.key === 'voucher').value,
                voucherAmount: bars.find((bar) => bar.key === 'voucher').amount,
                bars
            };
        });
        this.hasPaymentMethodOrders = this.paymentMethodReports.some((day) => day.total > 0);

        const today = this.paymentMethodReports[this.paymentMethodReports.length - 1];
        const todayMaximum = today
            ? today.bars.reduce((maximumValue, bar) => Math.max(maximumValue, bar.value), 0)
            : 0;
        this.todayPaymentMethodBars = today
            ? today.bars.map((bar) => ({
                key: bar.key,
                    label: bar.label,
                    value: bar.value,
                amount: bar.amount,
                    width: todayMaximum === 0 ? 0 : (bar.value / todayMaximum) * 100,
                    color: bar.color
                }))
            : [];
        this.todayPaymentOrders = today ? today.total : 0;
        this.todayPaymentAmount = today ? today.totalAmount : 0;
        this.hasTodayPaymentMethodOrders = this.todayPaymentOrders > 0;
    }

    formatAmount(value: number) {
        const amount = Number(value);
        return (isNaN(amount) ? 0 : amount).toFixed(2) + ' lei';
    }

    ngOnInit() {
        this.overviewData = this.dataService.getOverviewData();
        this.onlineCars = this.dataService.getOnlineCars();

        this.dataChangedOverviewSubscription = this.dataService.dataChangedOverview.subscribe(() => {

            this.overviewData = this.dataService.getOverviewData();
        });
        this.dataChangedOnlineCarsSubscription = this.dataService.dataChangedOnlineCars.subscribe(() => {

            this.onlineCars = this.dataService.getOnlineCars();
        });
        this.dataChangedSessionSubscription = this.dataService.dataChangedSession.subscribe(() => {

            this.loadPageData();
        });
        this.loadPageData();
    }

    ngOnDestroy() {
        if (!isNullOrUndefined(this.dataChangedOverviewSubscription)) {
            this.dataChangedOverviewSubscription.unsubscribe();
        }

        if (!isNullOrUndefined(this.dataChangedOnlineCarsSubscription)) {
            this.dataChangedOnlineCarsSubscription.unsubscribe();
        }

        if (!isNullOrUndefined(this.dataChangedSessionSubscription)) {
            this.dataChangedSessionSubscription.unsubscribe();
        }
    }

    loadPageData() {
        if (isNullOrUndefined(this.dataService.getSessionIdentifier())) {
            return;
        }

        this.getOverviewData();
        this.getOnlineCarsData();
        this.refreshOrderReports();
    }

    refreshStatus() {
        this.loadPageData();
    }

    getOverviewData() {
        this.restService.getOverviewData()
            .pipe(
                finalize(() => {
                    this.uiBlockerService.unBlockUI();
                })
            ).subscribe((res: any) => {
            if (this.dataService.isResponseSuccessful(res)) {
                this.dataService.setOverviewData(res.OverviewData);
            } else {
                // maybe show notification with error getting data
            }
        });
    }

    getOnlineCarsData() {
        this.restService.getOnlineCars()
            .pipe(
                finalize(() => {
                    this.uiBlockerService.unBlockUI();
                })
            ).subscribe((res: any) => {
            if (this.dataService.isResponseSuccessful(res)) {
                const onlineCars = {
                    OnlineCarsCount: res.OnlineCarsCount,
                    BusyCarsCount: res.BusyCarsCount
                };
                this.dataService.setOnlineCars(onlineCars);
            } else {
                // maybe show notification with error getting data
            }
        });
    }

    refreshOrderReports() {
        this.getTotalOrdersReport();
        this.getPaymentMethodOrdersReport();
    }

    private getLastSevenDays() {
        const days: Date[] = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (let offset = 6; offset >= 0; offset--) {
            const day = new Date(today);
            day.setDate(today.getDate() - offset);
            days.push(day);
        }
        return days;
    }

    private getLocalDateKey(date: Date) {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    private normalizePlatform(value: any) {
        const platform = String(value || '').trim().toLowerCase();
        switch (platform) {
            case 'oldios':
                return 'ios';
            case 'oldandroid':
                return 'android';
            case 'posandroid':
                return 'pos';
            case '':
                return 'unknown';
            default:
                return platform;
        }
    }

    private getPlatformOrder(platform: string) {
        const order = [
            'ios', 'android', 'broadcast', 'radio', 'manual', 'caller',
            'airport', 'kiosk', 'pos', 'driver', 'unknown'
        ];
        const index = order.indexOf(platform);
        return index < 0 ? order.length : index;
    }

    private getPlatformLabel(platform: string) {
        return getOrderSourceLabel(platform);
    }

    private getPlatformColor(platform: string, index: number) {
        const colors: { [platform: string]: string } = {
            ios: '#60a5fa',
            android: '#4ade80',
            broadcast: '#f97316',
            radio: '#c084fc',
            manual: '#facc15',
            caller: '#fb7185',
            airport: '#22d3ee',
            kiosk: '#a3e635',
            pos: '#f472b6',
            driver: '#818cf8',
            unknown: '#94a3b8'
        };
        const fallback = ['#2dd4bf', '#e879f9', '#fb923c', '#38bdf8', '#a78bfa'];
        return colors[platform] || fallback[index % fallback.length];
    }

}
